import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Plus, Copy, Trash2, Sparkles, Wand2, Eye } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { apiClient } from '@/services/api';
import { PlaylistPreviewModal } from '@/components/preview/PlaylistPreviewModal';

export function PlaylistsPage() {
  const qc = useQueryClient();
  const [previewId, setPreviewId] = useState<number | null>(null);

  const { data: playlists = [], isLoading } = useQuery({
    queryKey: ['playlists'],
    queryFn: () => apiClient.getPlaylists().then(r => r.data),
  });

  const { data: previewData } = useQuery({
    queryKey: ['playlist', previewId],
    queryFn: () => apiClient.getPlaylist(previewId!).then(r => r.data),
    enabled: !!previewId,
  });

  const dupMut = useMutation({
    mutationFn: (id: number) => apiClient.duplicatePlaylist(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['playlists'] }); toast.success('Playlist duplicada'); },
  });
  const delMut = useMutation({
    mutationFn: (id: number) => apiClient.deletePlaylist(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['playlists'] }); toast.success('Playlist removida'); },
  });

  return (
    <div className="container-app py-8">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold">Playlists</h1>
          <p className="text-muted-foreground">Sequências de slides para suas TVs</p>
        </div>
        <div className="flex gap-2">
          <Button asChild variant="gradient" size="lg" className="shadow-lg">
            <Link to="/playlists/templates">
              <Wand2 className="h-4 w-4" />
              Usar modelo pronto
            </Link>
          </Button>
          <Button asChild variant="outline">
            <Link to="/playlists/new">
              <Plus className="h-4 w-4" />Em branco
            </Link>
          </Button>
        </div>
      </div>

      {isLoading ? <div className="text-center py-12 text-muted-foreground">Carregando…</div>
        : playlists.length === 0 ? (
          <div className="rounded-2xl border-2 border-dashed border-brand-200 bg-brand-50/30 p-12 text-center">
            <div className="inline-flex items-center justify-center h-16 w-16 rounded-full gradient-brand mb-4">
              <Sparkles className="h-8 w-8 text-white" />
            </div>
            <h3 className="text-2xl font-bold">Comece com um modelo pronto</h3>
            <p className="text-muted-foreground mt-2 max-w-md mx-auto">
              Modelos prontos pra Açougue, Padaria, Mercado, Farmácia e mais.
              Já vêm com slides, preços e promoções estruturados — você só edita.
            </p>
            <div className="mt-6 flex justify-center gap-2">
              <Button asChild variant="gradient" size="lg" className="shadow-lg">
                <Link to="/playlists/templates">
                  <Wand2 className="h-5 w-5" />
                  Ver modelos
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg">
                <Link to="/playlists/new">Em branco</Link>
              </Button>
            </div>
          </div>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {playlists.map((p: any) => (
              <Card key={p.id} className="p-5 hover:shadow-md transition-all">
                <div className="flex items-start justify-between">
                  <Link to={`/playlists/${p.id}`} className="flex-1">
                    <div className="font-semibold">{p.name}</div>
                    <div className="text-xs text-muted-foreground mt-1">
                      {p.slide_count} slides · {Math.round(p.total_duration_ms / 1000)}s total
                    </div>
                  </Link>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <OrientationChip orientation={p.orientation} />
                    <Badge variant={p.is_active ? 'success' : 'muted'}>{p.is_active ? 'Ativa' : 'Inativa'}</Badge>
                  </div>
                </div>
                {p.description && <div className="text-sm text-muted-foreground mt-3 line-clamp-2">{p.description}</div>}
                <div className="flex gap-2 mt-4">
                  <Button asChild variant="outline" size="sm" className="flex-1"><Link to={`/playlists/${p.id}`}>Editar</Link></Button>
                  <Button variant="ghost" size="icon" onClick={() => setPreviewId(p.id)} title="Pré-visualizar"><Eye className="h-4 w-4" /></Button>
                  <Button variant="ghost" size="icon" onClick={() => dupMut.mutate(p.id)} title="Duplicar"><Copy className="h-4 w-4" /></Button>
                  <Button variant="ghost" size="icon" onClick={() => confirm(`Excluir "${p.name}"?`) && delMut.mutate(p.id)} title="Excluir"><Trash2 className="h-4 w-4" /></Button>
                </div>
              </Card>
            ))}
          </div>
        )}

      {/* Modal preview */}
      {previewId && previewData && (
        <PlaylistPreviewModal
          slides={previewData.slides ?? []}
          title={previewData.name}
          orientation={String(previewData.orientation ?? '').toLowerCase() === 'portrait' ? 'portrait' : 'landscape'}
          onClose={() => setPreviewId(null)}
        />
      )}
    </div>
  );
}

function OrientationChip({ orientation }: { orientation?: string | null }) {
  const isPortrait = String(orientation ?? '').toLowerCase() === 'portrait';
  return (
    <span
      title={isPortrait ? 'TV em pé (vertical)' : 'TV deitada (horizontal)'}
      className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wide ${
        isPortrait
          ? 'bg-amber-100 text-amber-800 dark:bg-amber-950/60 dark:text-amber-200'
          : 'bg-sky-100 text-sky-800 dark:bg-sky-950/60 dark:text-sky-200'
      }`}
    >
      <svg viewBox="0 0 24 24" className="h-3 w-3" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <g transform={isPortrait ? 'rotate(90 12 12)' : ''}>
          <rect x="2" y="5" width="20" height="12" rx="1.5" />
          <line x1="8" y1="20" x2="16" y2="20" />
          <line x1="12" y1="17" x2="12" y2="20" />
        </g>
      </svg>
      {isPortrait ? 'Vertical' : 'Horizontal'}
    </span>
  );
}
